import { BiqlQuery, QubesAPI } from "../qubes-client/api";
import { sqlFirewallQuery } from "../components/qubes-table/table-model";

/**
 * Unique identifier for a registered lookup definition.
 */
type LookupKey = string;

/**
 * Describes how a lookup dataset is fetched and mapped into cache.
 */
export type LookupDefinition = {
  /** Stable key used to register and retrieve the lookup. */
  key: LookupKey;
  /** BIQL query used to fetch lookup rows. Use `sql` instead for sql-firewall. */
  biqlQuery?: BiqlQuery;
  /** SQL string for sql-firewall queries. Takes precedence over biqlQuery. */
  sql?: string;
  /** Row field used as the option value and lookup key. */
  valueField: string;
  /** Row field used as the human-friendly display label. */
  labelField: string;
  /** Optional appSpace override; falls back to the query appSpace when omitted. */
  appSpace?: string;
};

/**
 * Normalized option shape used by select/combobox form items.
 */
export type LookupOption = {
  /** Raw key/identifier to persist. */
  value: string;
  /** Human-friendly text shown to users. */
  text: string;
};

/**
 * Static singleton cache for lookup datasets used by forms and table renderers.
 *
 * The cache stores resolved maps in-memory and de-duplicates concurrent fetches.
 * Consumers typically call {@link register} once during view setup and then use
 * {@link ensure}, {@link getLabel}, and {@link getOptionsSync} in rendering paths.
 */
export class LookupCache {
  private static readonly definitions = new Map<LookupKey, LookupDefinition>();
  private static readonly values = new Map<LookupKey, Map<string, string>>();
  private static readonly inFlight = new Map<
    LookupKey,
    Promise<Map<string, string>>
  >();

  /**
   * Registers or replaces a lookup definition for a given key.
   *
   * @param {LookupDefinition} definition Lookup definition describing how to load and map rows.
   */
  static register(definition: LookupDefinition): void {
    this.definitions.set(definition.key, definition);
  }

  /**
   * Clears cached values and any in-flight request for a specific lookup key.
   *
   * @param {LookupKey} key Registered lookup key.
   */
  static clear(key: LookupKey): void {
    this.values.delete(key);
    this.inFlight.delete(key);
  }

  /**
   * Clears all cached lookup values and in-flight requests.
   */
  static clearAll(): void {
    this.values.clear();
    this.inFlight.clear();
  }

  /**
   * Resolves a friendly label for a raw identifier.
   *
   * This method is asynchronous and will load lookup data on demand when not
   * yet cached.
   *
   * @param {LookupKey} key Registered lookup key.
   * @param {string | number | null | undefined} value Raw identifier to resolve.
   * @returns {Promise<string | undefined>} Resolved label when available.
   */
  static async getLabel(
    key: LookupKey,
    value: string | number | null | undefined,
  ): Promise<string | undefined> {
    if (value === null || value === undefined) {
      return undefined;
    }

    const cache = await this.ensure(key);
    return cache.get(String(value));
  }

  /**
   * Ensures lookup data is loaded and returns normalized options.
   *
   * @param {LookupKey} key Registered lookup key.
   * @returns {Promise<LookupOption[]>} Sorted list of lookup options.
   */
  static async getOptions(key: LookupKey): Promise<LookupOption[]> {
    const cache = await this.ensure(key);
    return Array.from(cache.entries())
      .map(([value, text]) => ({ value, text }))
      .sort((a, b) => a.text.localeCompare(b.text));
  }

  /**
   * Ensures a lookup map exists in cache.
   *
   * If data is already cached, returns it immediately. If a fetch is already
   * in progress for the same key, returns the existing promise.
   *
   * @param {LookupKey} key Registered lookup key.
   * @returns {Promise<Map<string, string>>} Map of raw identifier -> friendly label.
   * @throws Error when no lookup definition has been registered for the key.
   */
  static async ensure(key: LookupKey): Promise<Map<string, string>> {
    const cached = this.values.get(key);
    if (cached) {
      return cached;
    }

    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }

    const definition = this.definitions.get(key);
    if (!definition) {
      throw new Error(`Lookup definition not found for key: ${key}`);
    }

    const loadPromise = this.load(definition).finally(() => {
      this.inFlight.delete(key);
    });

    this.inFlight.set(key, loadPromise);
    return loadPromise;
  }

  /**
   * Executes the lookup BIQL query and materializes results into cache format.
   *
   * @param {LookupDefinition} definition Lookup fetch/mapping definition.
   * @returns {Promise<Map<string, string>>} Resolved lookup map to store in cache.
   */
  private static async load(
    definition: LookupDefinition,
  ): Promise<Map<string, string>> {
    let rows: Record<string, unknown>[];

    if (definition.sql) {
      rows = await sqlFirewallQuery(definition.sql);
    } else if (definition.biqlQuery) {
      const query = definition.biqlQuery.clone();
      const response = await QubesAPI.get().biqlQuery<Record<string, unknown>>(
        query.buildQueryString(),
        definition.appSpace ?? query.appSpace(),
        "verbose",
      );
      rows = response?.data ?? [];
    } else {
      throw new Error(`Lookup definition "${definition.key}" has neither sql nor biqlQuery`);
    }

    const map = new Map<string, string>();

    rows.forEach((row) => {
      const keyValue = row[definition.valueField];
      if (keyValue === undefined || keyValue === null) {
        return;
      }

      const labelValue = row[definition.labelField];
      map.set(String(keyValue), labelValue == null ? "" : String(labelValue));
    });

    this.values.set(definition.key, map);
    return map;
  }
}
